// src/features/planning/pages/MasterScheduleCreatePage.tsx
// ADR-092 Rule PLN-FE-02.
//
// ⚠️ Hook names inferred from ADR-082 pattern — verify against real
// orval output (ADR-092 Rule PLN-FE-06). The backend
// MasterScheduleService.create_draft (ADR-090 Rule PLN-05) derives the
// Family from `code`/`name` via get_or_create, so this single form
// creates both the Family and its first draft Revision in one call —
// mirrors BomFamilyCreatePage's simplicity (ADR-082).

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useV1PlanningMasterScheduleRevisionsCreate } from "@/api/generated/v1/v1";
import DatePicker, { DateObject } from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import { extractData } from "@/shared/utils/apiResponseUtils";
import { PageBanner } from "@/shared/components/PageBanner";

export function MasterScheduleCreatePage() {
  const navigate = useNavigate();
  const { mutateAsync, isPending } =
    useV1PlanningMasterScheduleRevisionsCreate();

  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [horizonStart, setHorizonStart] = useState("");
  const [horizonEnd, setHorizonEnd] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      // NOTE: perform_create on the backend ViewSet expects `family`
      // (validated_data["family"]) to resolve code/name — see
      // MasterScheduleRevisionViewSet.perform_create in ADR-090's API
      // layer. The exact request shape (family as nested object vs.
      // separate family-create call first) must be confirmed against
      // the serializer once orval types exist; this assumes a combined
      // payload as designed.
      const rawRevision = await mutateAsync({
        data: { tenant_id: "", revision_no: 1, family: { code, name } as any as string, horizon_start: horizonStart, horizon_end: horizonEnd } as any
      });
      const revision = extractData<any>(rawRevision);
      navigate(`/planning/revisions/${revision?.id}`);
    } catch (err) {
      setError("ایجاد برنامه با خطا مواجه شد. ورودی‌ها را بررسی کنید.");
    } finally {
      // setIsPending(false);
    }
  }

  return (
    <div style={{ padding: "0.5rem 0", maxWidth: "100%", margin: "0", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <PageBanner
        title="ایجاد برنامهٔ اصلی تولید جدید"
        subtitle="تعریف بازه زمانی و مشخصات کلی برنامه کلان"
        icon="🗓️"
        helpLink="/help/planning/master-schedule-create"
      >
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              padding: "0.6rem 1rem",
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.15)",
              color: "#ffffff",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              fontWeight: 700,
              fontSize: "0.85rem",
              cursor: "pointer",
            }}
          >
            ← انصراف
          </button>
        </div>
      </PageBanner>

      <div className="brand-card-extand" style={{ maxWidth: "600px", margin: "0 auto", padding: "2rem", width: "100%" }}>
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
          <div>
            <label htmlFor="master-schedule-code" style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
              کد برنامه
            </label>
            <input
              id="master-schedule-code"
              className="input-field"
              style={{ width: "100%" }}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              placeholder="مثال: MPS-2026-Q4"
            />
          </div>
          <div>
            <label htmlFor="master-schedule-name" style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
              نام برنامه
            </label>
            <input
              id="master-schedule-name"
              className="input-field"
              style={{ width: "100%" }}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
              شروع بازه
            </label>
            <div style={{ position: "relative" }}>
              <DatePicker
                calendar={persian}
                locale={persian_fa}
                value={horizonStart ? new Date(horizonStart) : null}
                onChange={(date: DateObject | null) => setHorizonStart(date ? date.toDate().toISOString().split('T')[0] ?? "" : "")}
                inputClass="input-field"
                containerStyle={{ width: "100%" }}
              />
            </div>
          </div>
          <div>
            <label style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
              پایان بازه
            </label>
            <div style={{ position: "relative" }}>
              <DatePicker
                calendar={persian}
                locale={persian_fa}
                value={horizonEnd ? new Date(horizonEnd) : null}
                onChange={(date: DateObject | null) => setHorizonEnd(date ? date.toDate().toISOString().split('T')[0] ?? "" : "")}
                inputClass="input-field"
                containerStyle={{ width: "100%" }}
              />
            </div>
          </div>

          {error && <p style={{ color: "#ef4444", fontSize: "0.9rem" }}>{error}</p>}

          <button className="btn btn-primary" type="submit" disabled={isPending} style={{ marginTop: "1rem", width: "100%" }}>
            {isPending ? "در حال ایجاد..." : "ایجاد و ادامه"}
          </button>
        </form>
      </div>
    </div>
  );
}
